import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import ScrollToTop from "@/components/ScrollToTop";
import AppShellSkeleton from "@/components/layout/AppShellSkeleton";

// Landing page (eager – it's the entry point)
import LandingPageV3 from "./pages/LandingPageV3";

// Testbädd v2 för ny startsidesversion (nås endast via direkt URL)
const HomeV2 = lazy(() => import("./pages/HomeV2"));

// Auth pages – lazy
const LoginPage = lazy(() => import("./pages/auth/LoginPage"));
const SignupPage = lazy(() => import("./pages/auth/SignupPage"));
const VerifieraEmailPage = lazy(() => import("./pages/auth/VerifieraEmailPage"));

// App layout & pages – lazy
const AppLayout = lazy(() => import("./components/layout/AppLayout"));
const DashboardPage = lazy(() => import("./pages/app/DashboardPage"));
const SocialCalendarPage = lazy(() => import("./pages/app/SocialCalendarPage"));
const TopicsPage = lazy(() => import("./pages/app/TopicsPage"));
const ContentStrategyPage = lazy(() => import("./pages/app/ContentStrategyPage"));
const ContentListPage = lazy(() => import("./pages/app/ContentListPage"));
const ContentEditorPage = lazy(() => import("./pages/app/ContentEditorPage"));
const AssetsPage = lazy(() => import("./pages/app/AssetsPage"));
const OAuthCallbackPage = lazy(() => import("./pages/app/OAuthCallbackPage"));
const CompanyProfilePage = lazy(() => import("./pages/app/CompanyProfilePage"));
const BrandKitPage = lazy(() => import("./pages/app/BrandKitPage"));
const KontoPage = lazy(() => import("./pages/app/KontoPage"));
const ForetagPage = lazy(() => import("./pages/app/ForetagPage"));
const PublishQueuePage = lazy(() => import("./pages/app/PublishQueuePage"));
const PublishHistoryPage = lazy(() => import("./pages/app/PublishHistoryPage"));
const SetupCompletePage = lazy(() => import("./pages/app/SetupCompletePage"));

// V3 sub-pages – lazy
const PriserPage = lazy(() => import("./pages/v3/PriserPage"));
const FunktionerPage = lazy(() => import("./pages/v3/FunktionerPage"));
const SaFunkarDetPage = lazy(() => import("./pages/v3/SaFunkarDetPage"));
const BloggPage = lazy(() => import("./pages/v3/BloggPage"));
const BlogPostPage = lazy(() => import("./pages/v3/BlogPostPage"));
const OmOssPage = lazy(() => import("./pages/v3/OmOssPage"));
const KontaktPage = lazy(() => import("./pages/v3/KontaktPage"));
const VillkorPage = lazy(() => import("./pages/v3/VillkorPage"));
const IntegritetspolicyPage = lazy(() => import("./pages/v3/IntegritetspolicyPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,            // 1 min — data anses färsk
      gcTime: 10 * 60_000,          // 10 min i minnet
      refetchOnWindowFocus: false,  // ingen onödig refetch vid tabbyte
      refetchOnMount: false,        // använd cache om den finns
      retry: 1,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<AppShellSkeleton />}>
          <Routes>
            {/* Auth routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/signup/verifiera" element={<VerifieraEmailPage />} />
            
            {/* Landing page */}
            <Route path="/" element={<LandingPageV3 />} />

            {/* Intern testbädd för ny startsida (ej länkad i nav) */}
            <Route path="/home-2" element={<HomeV2 />} />

            {/* Redirects from old routes */}
            <Route path="/v1" element={<Navigate to="/" replace />} />
            <Route path="/lomoolo" element={<Navigate to="/" replace />} />
            <Route path="/pricing" element={<Navigate to="/priser" replace />} />
            <Route path="/privacy" element={<Navigate to="/integritetspolicy" replace />} />
            <Route path="/terms" element={<Navigate to="/villkor" replace />} />

            {/* V3 sub-pages */}
            <Route path="/priser" element={<PriserPage />} />
            <Route path="/funktioner" element={<FunktionerPage />} />
            <Route path="/sa-funkar-det" element={<SaFunkarDetPage />} />
            <Route path="/blogg" element={<BloggPage />} />
            <Route path="/blogg/:slug" element={<BlogPostPage />} />
            <Route path="/om-oss" element={<OmOssPage />} />
            <Route path="/kontakt" element={<KontaktPage />} />
            <Route path="/villkor" element={<VillkorPage />} />
            <Route path="/integritetspolicy" element={<IntegritetspolicyPage />} />
            
            {/* App routes */}
            <Route path="/app/onboarding" element={<Navigate to="/app/dashboard" replace />} />
            <Route path="/app/klart" element={<SetupCompletePage />} />
            <Route path="/app" element={<AppLayout />}>
              <Route index element={<Navigate to="/app/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="calendar/social" element={<SocialCalendarPage />} />
              <Route path="topics" element={<TopicsPage />} />
              <Route path="strategy" element={<ContentStrategyPage />} />
              <Route path="content" element={<ContentListPage />} />
              <Route path="content/:id" element={<ContentEditorPage />} />
              <Route path="assets" element={<AssetsPage />} />
              <Route path="publish/queue" element={<PublishQueuePage />} />
              <Route path="publish/history" element={<PublishHistoryPage />} />
              <Route path="admin/integrations" element={<Navigate to="/app/foretag?tab=integrations" replace />} />
              <Route path="admin/integrations/callback/:provider" element={<OAuthCallbackPage />} />
              <Route path="admin/team" element={<Navigate to="/app/foretag?tab=team" replace />} />
              <Route path="admin/company" element={<CompanyProfilePage />} />
              <Route path="admin/brand-kit" element={<BrandKitPage />} />
              <Route path="admin/account" element={<Navigate to="/app/konto" replace />} />
              <Route path="konto" element={<KontoPage />} />
              <Route path="foretag" element={<ForetagPage />} />
            </Route>
            
            {/* Catch-all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
